import React from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { environment } from './environment';
import { EnvironmentName } from './types';
import { isDev, isLocalhost, isProd, isStag } from './utils/helpers/environment';
import { isLocalApp } from './utils/helpers/local-app';

const getEnvironment = () => {
  if (isDev) return EnvironmentName.Dev;
  if (isStag) return EnvironmentName.Stage;
  if (isProd) return EnvironmentName.Prod;

  return EnvironmentName.Localhost;
};

export const initSentry = () => {
  if (isLocalApp || isLocalhost) return;

  Sentry.init({
    dsn: environment.SENTRY_DSN,
    tracesSampleRate: 0.3,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    environment: getEnvironment(),
    release: environment.BUILD_ID,
    denyUrls: [/node_modules/, /chrome-extension:\/\/nkbihfbeogaeaoehlefnkodbefgpgknn/],
    ignoreErrors: [
      /Failed to fetch/,
      /NetworkError/,
      /The connection was stopped during negotiation/,
      /Non-Error promise rejection captured with value/,
      /Connection is not established/,
      /Response timeout/,
      /The current user is not authorized to access this resource/,
      /A timeout occurred/,
      /Invariant Violation/,
      /ApolloError/,
      /Failed to send request, resource/,
      /InvalidAccessError/,
      /WebSocket failed to connect/,
      /Failed to start the HttpConnection/,
      /WebSocket closed with status code: 1006/,
      /Server timeout elapsed without receiving a message from the server/,
      /Failed to start the HttpConnection before stop() was called/,
      /Failed to complete negotiation/,
      /Failed to send request/,
      /Node.removeChild: The node to be removed is not a child of this node/,
      /Connection lost/,
      /Server responded with an invalid status/,
      /AbortError/,

      // Vite/Webpack chunk loading errors
      // /Unable to preload CSS/,
      // /Loading CSS chunk .* failed/,
      // /Loading chunk .* failed/,
      // /ChunkLoadError/,

      //lavva.exalushome
      /Failed to get groups, API responded with status 13 data: null/,
      /Cannot get runtime info, controller response timeout/,
      /Timeout: Failed to connect to device within 5 seconds/,
      /Failed to restore connection/,
      /Cannot send data if the connection is not in the 'Connected' State/,
      /UpdateProviderNotFound/,
      /Cannot get update providers! Requested update providers has not been registered/,
      /Wrong authentication data/,
      /Server returned handshake error: Handshake was canceled/,

      // Third-party
      /Failed to connect to MetaMask/,
      /MetaMask extension not found/,
    ],
    beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
      console.log('SENTRY EVENT: ', { EVENT: JSON.stringify(event), HINT: JSON.stringify(hint) });
      
      // Filter out errors from node_modules
      const frames = event.exception?.values?.[0]?.stacktrace?.frames;
      if (frames && frames.some((frame) => frame.filename?.includes('node_modules'))) {
        return null;
      }
      
      return event;
    },
  });
};
